import React from 'react';

const PrimePathPage = () => {
    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 antialiased">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 py-20 md:py-32 px-6 overflow-hidden rounded-b-3xl shadow-2xl">
                {/* Abstract background shapes */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                    <div className="absolute w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob top-10 left-10"></div>
                    <div className="absolute w-64 h-64 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000 top-20 right-20"></div>
                    <div className="absolute w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000 bottom-10 left-1/4"></div>
                </div>

                <div className="relative z-10 max-w-6xl mx-auto">
                    <div className="flex flex-col lg:flex-row items-center justify-between">
                        {/* Text Content */}
                        <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0">
                            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-indigo-300 to-blue-300 drop-shadow-lg">
                                Prime Path
                            </h1>
                            <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl">
                                Master the art of prime numbers in this captivating puzzle game. Merge tiles strategically to reach the ultimate goal of 2357!
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.lvlmania.prime_path&pcampaignid=web_share"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 flex items-center justify-center"
                                >
                                    <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                                    </svg>
                                    Download on Google Play
                                </a>
                                <button
                                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50"
                                    onClick={() => {
                                        window.location.href = '/2357-prime';
                                    }}
                                >
                                    Play Web Demo
                                </button>
                            </div>
                        </div>

                        {/* App Screenshot/Mockup */}
                        <div className="lg:w-1/2 flex justify-center">
                            <div className="relative">
                                <div className="w-64 h-96 bg-gray-800 rounded-3xl shadow-2xl border-8 border-gray-700 p-4">
                                    <div className="w-full h-full bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl flex flex-col items-center justify-center">
                                        <div className="text-white text-2xl font-bold mb-4">Prime Path</div>
                                        <div className="grid grid-cols-4 gap-1 mb-4">
                                            {[2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53].map((num, index) => (
                                                <div
                                                    key={index}
                                                    className="w-8 h-8 bg-purple-600 text-white text-xs font-bold rounded flex items-center justify-center"
                                                >
                                                    {num}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-yellow-400 text-lg font-bold">Target: 2357</div>
                                    </div>
                                </div>
                                {/* Phone notch */}
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-6 bg-gray-700 rounded-b-lg"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Game Features Section */}
            <section className="py-16 md:py-24 px-6 max-w-7xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-12 text-purple-400 drop-shadow-md">
                    Game Features
                </h2>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Feature 1 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-purple-400 text-4xl mb-4">🧮</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Prime Number Mastery</h3>
                        <p className="text-gray-400">
                            Learn and master prime numbers through strategic tile merging. Challenge your mathematical thinking!
                        </p>
                    </div>

                    {/* Feature 2 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-indigo-400 text-4xl mb-4">🧠</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Strategic Thinking</h3>
                        <p className="text-gray-400">
                            Plan your moves carefully to reach the ultimate target of 2357. Every move counts!
                        </p>
                    </div>

                    {/* Feature 3 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-blue-400 text-4xl mb-4">📱</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Mobile Optimized</h3>
                        <p className="text-gray-400">
                            Smooth touch controls and intuitive swipe gestures make playing on mobile a breeze.
                        </p>
                    </div>

                    {/* Feature 4 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-green-400 text-4xl mb-4">🎯</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Progressive Difficulty</h3>
                        <p className="text-gray-400">
                            Start with simple primes and work your way up to more complex number combinations.
                        </p>
                    </div>

                    {/* Feature 5 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-yellow-400 text-4xl mb-4">🏆</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Achievement System</h3>
                        <p className="text-gray-400">
                            Unlock achievements as you reach higher primes and master different strategies.
                        </p>
                    </div>

                    {/* Feature 6 */}
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 transform transition duration-300 hover:scale-105">
                        <div className="text-pink-400 text-4xl mb-4">🎨</div>
                        <h3 className="text-2xl font-bold text-white mb-3">Beautiful Design</h3>
                        <p className="text-gray-400">
                            Enjoy a sleek, modern interface with smooth animations and visual feedback.
                        </p>
                    </div>
                </div>
            </section>

            {/* How to Play Section */}
            <section className="bg-gray-800 py-16 md:py-24 px-6 rounded-3xl shadow-2xl mx-4 mb-8">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-12 text-purple-400">How to Play</h2>
                    
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                        <div className="space-y-6">
                            <div className="flex items-start space-x-4">
                                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Swipe to Move</h3>
                                    <p className="text-gray-300">Use swipe gestures to move tiles in any direction. Tiles will slide until they hit a wall or another tile.</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start space-x-4">
                                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Merge Prime Numbers</h3>
                                    <p className="text-gray-300">When two tiles with the same number touch, they merge into the next prime number in sequence.</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start space-x-4">
                                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Reach 2357</h3>
                                    <p className="text-gray-300">Your goal is to create a tile with the number 2357. Plan your moves strategically!</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start space-x-4">
                                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">Special Tiles</h3>
                                    <p className="text-gray-300">Purple tiles have special properties and help you create larger prime numbers more efficiently.</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-center">
                            <div className="bg-gray-700 rounded-2xl p-6 shadow-xl">
                                <div className="grid grid-cols-4 gap-2 mb-4">
                                    {[2, 3, 5, 7, null, null, 11, 13, null, 17, null, 19, null, null, null, 23].map((num, index) => (
                                        <div
                                            key={index}
                                            className={`w-12 h-12 flex items-center justify-center rounded-lg font-bold text-sm ${
                                                num ? 'bg-purple-600 text-white' : 'bg-gray-600'
                                            }`}
                                        >
                                            {num || ''}
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center text-gray-300 text-sm">Example Game Board</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Download CTA Section */}
            <section className="py-16 md:py-24 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">Ready to Challenge Your Mind?</h2>
                    <p className="text-lg md:text-xl text-gray-300 mb-10">
                        Download Prime Path now and start your journey to mathematical mastery!
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <a
                            href="https://play.google.com/store/apps/details?id=com.lvlmania.prime_path&pcampaignid=web_share"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 flex items-center justify-center"
                        >
                            <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                            </svg>
                            Download for Android
                        </a>
                        <button
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50"
                            onClick={() => {
                                window.location.href = '/2357-prime';
                            }}
                        >
                            Try Web Demo
                        </button>
                    </div>
                    
                    <div className="mt-8 text-center">
                        <p className="text-gray-400 mb-2">
                            Have questions or feedback? Contact us:
                        </p>
                        <a
                            href="mailto:support@lvlmania.com"
                            className="text-purple-400 hover:text-purple-500 font-bold transition duration-300"
                        >
                            support@lvlmania.com
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrimePathPage;